import type { FC } from "react";
import type { QuoteResponse } from "./quotesApi";
import { Alert, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import dayjs from "../dayjs";
import type { PriceValue } from "../misc/price";
import type { Currency } from "../data/BaseEntity";

type FormState = Pick<QuoteResponse, "products">;
type QuoteProduct = FormState["products"][number];

interface QuoteProductRowProps {
  readonly product: QuoteProduct;
}

function priceDisplayValue(price: PriceValue, currency: Currency) {
  return `${price} ${currency}`;
}

const QuoteProductRow: FC<QuoteProductRowProps> = ({ product }) => {
  const optionRows = product.options.slice(1);
  return (
    <>
      <TableRow>
        <TableCell rowSpan={product.options.length}>{product.date}</TableCell>
        <TableCell rowSpan={product.options.length}>{product.name}</TableCell>
        <TableCell>{product.options[0].name}</TableCell>
        <TableCell>{product.options[0].quantity}</TableCell>
        <TableCell>{priceDisplayValue(product.options[0].price, product.currency)}</TableCell>
      </TableRow>
      {optionRows.map((o) => (
        <TableRow key={`${product.id}:${o.id}`}>
          <TableCell>{o.name}</TableCell>
          <TableCell>{o.quantity}</TableCell>
          <TableCell>{priceDisplayValue(o.price, product.currency)}</TableCell>
        </TableRow>
      ))}
    </>
  );
};

const QuotePreparedAlert: FC<{ prepared: NonNullable<QuoteResponse["prepared"]> }> = ({ prepared }) => {
  return (
    <Alert icon={<InfoIcon fontSize="inherit" />} severity="info" sx={{ marginBottom: 1 }}>
      {`Wycena została już przygotowana ${dayjs(prepared.at).format("DD.MM.YYYY")} przez ${prepared.by}. Nie można jej
      modyfikować.`}
    </Alert>
  );
};

interface Props {
  readonly quote: QuoteResponse;
}

const QuoteView: FC<Props> = ({ quote }) => (
  <Grid
    container
    spacing={2}
    sx={{
      marginTop: 1,
      marginBottom: 1,
    }}
  >
    {quote.prepared && (
      <Grid size={12}>
        <QuotePreparedAlert prepared={quote.prepared} />
      </Grid>
    )}
    <Grid size={12}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Produkt</TableCell>
            <TableCell>Opcja</TableCell>
            <TableCell>Ilość</TableCell>
            <TableCell>Cena</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {quote.products.map((p) => (
            <QuoteProductRow key={`${p.id}:${p.date}`} product={p} />
          ))}
        </TableBody>
      </Table>
    </Grid>
    {quote.additionalInfo && (
      <Grid size={12} spacing={1}>
        <Typography variant="subtitle2">Dodatkowe informacje</Typography>
        <Typography>{quote.additionalInfo}</Typography>
      </Grid>
    )}
  </Grid>
);

export default QuoteView;
